import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Icon } from "@/components/common/Icon";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { CATEGORIES, CLIENTS, PROCESS, PROJECTS } from "@/data/site";
import { ProjectCard } from "@/pages/public/Projects";

const FEATURED = CATEGORIES.filter((c) => c.featured);

const CategoryCard = ({ c, large, wide }) => (
  <Link to={`/solutions/${c.slug}`} data-testid={`home-category-${c.slug}`} className={`group relative flex overflow-hidden rounded-xl border border-slate-200 bg-slate-900 ${large ? "min-h-[420px] md:row-span-2" : "min-h-[200px]"} ${wide ? "md:col-span-3" : ""}`}>
    <img src={c.cover} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#123634] via-[#123634]/60 to-transparent" />
    <div className="relative mt-auto w-full p-6">
      <div className="flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur"><Icon name={c.icon} className="h-5 w-5 text-white" /></span>
        <ArrowUpRight className="h-5 w-5 text-white/70 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white" />
      </div>
      <h3 className="mt-4 text-xl font-semibold text-white">{c.name}</h3>
      <p className={`mt-2 text-sm text-slate-300 ${large ? "" : "line-clamp-2"}`}>{c.short}</p>
    </div>
  </Link>
);

export const SolutionsBento = () => (
  <section className="bg-[#F4F6F2] py-24" data-testid="home-solutions">
    <div className="container-x">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading eyebrow="Solution categories" title="Every layer of fire protection, from one accountable partner." text="Positioned by risk and use case — not a catalogue dump. Tell us the site; we specify the system." />
        <Link to="/solutions" className="btn-outline shrink-0" data-testid="home-view-all-solutions">View all solutions</Link>
      </div>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {FEATURED.map((c, i) => <CategoryCard key={c.slug} c={c} large={i === 0} wide={i === FEATURED.length - 1} />)}
      </div>
    </div>
  </section>
);

export const ProcessStrip = () => (
  <section className="bg-white py-24" data-testid="home-process">
    <div className="container-x">
      <SectionHeading eyebrow="How we engage" title="Consultative from survey to sign-off." />
      <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 md:grid-cols-4">
        {PROCESS.map((p, i) => (
          <Reveal key={p.step} delay={i * 0.08} className="bg-white p-8">
            <p className="mono text-sm font-semibold text-[#C20500]">{p.step}</p>
            <h3 className="mt-6 text-lg font-semibold text-slate-900">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ClientStrip = () => (
  <section className="border-y border-slate-200 bg-white py-14" data-testid="home-clients">
    <p className="mono text-center text-[11px] uppercase tracking-[0.22em] text-slate-500">Trusted by leading organisations across India</p>
    <div className="relative mt-8 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
      <div className="animate-marquee flex w-max items-center gap-12">
        {[...CLIENTS, ...CLIENTS].map((c, i) => (
          c.logo ? (
            <img key={i} src={c.logo} alt={c.name} loading="lazy" className="h-14 max-w-[150px] w-auto shrink-0 object-contain mix-blend-multiply opacity-80 grayscale transition-[filter,opacity] duration-300 hover:opacity-100 hover:grayscale-0" data-testid={`client-logo-${i}`} />
          ) : (
            <span key={i} className="whitespace-nowrap text-xl font-bold tracking-[0.2em] text-slate-300 transition-colors hover:text-slate-600" style={{ fontFamily: "Fraunces" }}>{c.name}</span>
          )
        ))}
      </div>
    </div>
  </section>
);

export const ProjectsTeaser = () => (
  <section className="bg-[#F4F6F2] py-24" data-testid="home-projects">
    <div className="container-x">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading eyebrow="Project showcase" title="Protecting hospitals, campuses and warehouses across India." />
        <Link to="/projects" className="btn-outline shrink-0" data-testid="home-view-all-projects">View all projects</Link>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {PROJECTS.slice(0, 3).map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.08}>
            <Link to="/projects" className="block"><ProjectCard p={p} /></Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
